/**
 * Phase 1.3 — Global client-side transport decompression for the in-payload card-definition
 * dictionary (see modules/battleWsCardDefs.js + ./battleWsCardDefs.js).
 *
 * The server compresses full-gameState messages on EVERY game WebSocket connection
 * (wrapGameWebSocketSend is applied to every connection). Battle gameState is consumed by many
 * client entry points — BattleUI, spectator BattleOverview, the autobattler/bot views, campaign,
 * proving-grounds, explore-unknown, the matchmaking match-found forwarder, and the sync monitor —
 * and forwarded into BattleUI as initial state through props/route handoffs. Rather than sprinkle
 * an expand() call across ~13 fragile sites (and risk missing a future one), we decompress once at
 * the transport boundary so every handler receives already-expanded event.data.
 *
 * Safety:
 *  - Strict pass-through for any message whose text does not contain the `"cardDefs"` token, so
 *    non-battle sockets (friends, matchmaking chatter, etc.) are completely unaffected.
 *  - Expansion mutates a real MessageEvent by shadowing only its `data` property (own value shadows
 *    the prototype accessor), so all other event getters keep working with the genuine event as
 *    receiver — no synthetic events, no illegal-invocation risk.
 *  - The original (compressed) byte length is stashed on the event so the perf HUD keeps reporting
 *    the true wire size.
 */

import { decompressGameStateCardDefs } from './battleWsCardDefs';

const CARD_DEFS_TOKEN = '"cardDefs"';
const WIRE_BYTES_PROP = '__codaWireByteLength';
const LISTENER_WRAP = Symbol('codaCardDefsWsMessageWrapper');

let installed = false;

function expandMessageEventInPlace(event) {
  if (!event || typeof event !== 'object') {
    return event;
  }
  const raw = event.data;
  if (typeof raw !== 'string' || raw.indexOf(CARD_DEFS_TOKEN) === -1) {
    return event;
  }

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return event;
  }
  if (
    !parsed ||
    typeof parsed !== 'object' ||
    !parsed.gameState ||
    typeof parsed.gameState !== 'object' ||
    !parsed.gameState.cardDefs
  ) {
    return event;
  }

  decompressGameStateCardDefs(parsed.gameState);
  const expanded = JSON.stringify(parsed);

  try {
    Object.defineProperty(event, WIRE_BYTES_PROP, {
      value: raw.length,
      configurable: true,
      writable: true,
      enumerable: false,
    });
    Object.defineProperty(event, 'data', {
      value: expanded,
      configurable: true,
      writable: true,
      enumerable: true,
    });
    return event;
  } catch {
    // Frozen/sealed event (should not happen for native MessageEvent) — deliver a real synthetic
    // MessageEvent carrying the expanded payload so handlers still receive correct data.
    const synthetic = new MessageEvent('message', {
      data: expanded,
      origin: event.origin,
      lastEventId: event.lastEventId,
      source: event.source,
      ports: event.ports,
    });
    try {
      synthetic[WIRE_BYTES_PROP] = raw.length;
    } catch {
      /* non-fatal: perf HUD wire-size just falls back to expanded size */
    }
    return synthetic;
  }
}

function wrapMessageListener(listener) {
  if (typeof listener !== 'function') {
    return listener;
  }
  return function cardDefsExpandingListener(event) {
    return listener.call(this, expandMessageEventInPlace(event));
  };
}

/**
 * Idempotently patch WebSocket so incoming compressed battle messages are expanded before any
 * application handler runs. Safe to call multiple times; only the first call installs.
 */
export function installBattleWsCardDefsDecompression() {
  if (installed) {
    return;
  }
  if (typeof WebSocket === 'undefined' || !WebSocket.prototype) {
    return;
  }
  installed = true;

  const proto = WebSocket.prototype;

  // 1) onmessage assignment path (`socket.onmessage = fn`) — the common case in this app.
  const onmessageDesc = Object.getOwnPropertyDescriptor(proto, 'onmessage');
  if (onmessageDesc && typeof onmessageDesc.set === 'function' && typeof onmessageDesc.get === 'function') {
    const originalSet = onmessageDesc.set;
    const originalGet = onmessageDesc.get;
    const ORIGINAL_HANDLER = Symbol('codaCardDefsOriginalOnmessage');
    Object.defineProperty(proto, 'onmessage', {
      configurable: true,
      enumerable: onmessageDesc.enumerable === true,
      get() {
        // Preserve identity for app code that reads back the handler it assigned.
        return this[ORIGINAL_HANDLER] !== undefined ? this[ORIGINAL_HANDLER] : originalGet.call(this);
      },
      set(fn) {
        this[ORIGINAL_HANDLER] = fn;
        originalSet.call(this, typeof fn === 'function' ? wrapMessageListener(fn) : fn);
      },
    });
  }

  // 2) addEventListener('message', fn) path — wrap the listener and remember the wrapper on the
  //    original function so removeEventListener can still detach it.
  const originalAdd = proto.addEventListener;
  const originalRemove = proto.removeEventListener;
  if (typeof originalAdd === 'function') {
    proto.addEventListener = function addEventListenerWithCardDefs(type, listener, ...args) {
      if (type === 'message' && typeof listener === 'function') {
        let wrapped = listener[LISTENER_WRAP];
        if (!wrapped) {
          wrapped = wrapMessageListener(listener);
          try {
            Object.defineProperty(listener, LISTENER_WRAP, {
              value: wrapped,
              configurable: true,
              writable: true,
              enumerable: false,
            });
          } catch {
            /* if we cannot cache the wrapper, removeEventListener(original) simply won't detach it */
          }
        }
        return originalAdd.call(this, type, wrapped, ...args);
      }
      return originalAdd.call(this, type, listener, ...args);
    };
  }
  if (typeof originalRemove === 'function') {
    proto.removeEventListener = function removeEventListenerWithCardDefs(type, listener, ...args) {
      if (type === 'message' && typeof listener === 'function' && listener[LISTENER_WRAP]) {
        return originalRemove.call(this, type, listener[LISTENER_WRAP], ...args);
      }
      return originalRemove.call(this, type, listener, ...args);
    };
  }
}

export { WIRE_BYTES_PROP };
